/**
 * @file shipping-method-labels.ts
 *
 * Localized-label vocabulary for the zone-form method UI: resolving a method type
 * (`flat_rate`/`free_shipping`) and a free-shipping `requires` value to their
 * message descriptors. The maps are module-private; callers use the accessors so
 * the fallbacks live in one place — mirroring the orders/coupons `*-status`
 * vocabulary split.
 */

import type { MessageDescriptor } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { ShippingMethodType } from './custom-shipping-form-types';

/** Label descriptor per method type. */
const METHOD_TYPE_LABELS: Record<ShippingMethodType, MessageDescriptor> = {
  flat_rate: adminMessages.shippingMethodFlatRate,
  free_shipping: adminMessages.shippingMethodFreeShipping,
};

/** Label descriptor per free-shipping `requires` value. */
const REQUIREMENT_LABELS: Record<string, MessageDescriptor> = {
  '': adminMessages.shippingRequiresNone,
  coupon: adminMessages.shippingRequiresCoupon,
  min_amount: adminMessages.shippingRequiresMinAmount,
  either: adminMessages.shippingRequiresEither,
  both: adminMessages.shippingRequiresBoth,
};

/**
 * Resolves a method type to its localized label descriptor.
 *
 * @param methodType - The method type.
 *
 * @returns The label {@link MessageDescriptor}.
 */
export function methodTypeLabel(methodType: ShippingMethodType): MessageDescriptor {
  return METHOD_TYPE_LABELS[methodType];
}

/**
 * Resolves a free-shipping `requires` value to its localized label descriptor,
 * falling back to the "no requirement" label for an unknown value.
 *
 * @param value - The `requires` value.
 *
 * @returns The label {@link MessageDescriptor}.
 */
export function requirementLabel(value: string): MessageDescriptor {
  return REQUIREMENT_LABELS[value] ?? adminMessages.shippingRequiresNone;
}
